import React, { useState, useEffect } from 'react';
import apiService, { API_BASE_URL } from '../services/api';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { Button, Select } from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import './AdminLandingPages.css';

const getDefaultDateTime = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const DEFAULT_FORM_STATE = {
  target_url: '',
  media_file_ids: [],
  get start_date() { return getDefaultDateTime(); },
  get end_date() { return getDefaultDateTime(); },
  content_config: {
    slidesMeta: {}
  }
};

export default function AdminLandingPages() {
  const [pages, setPages] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState(JSON.parse(JSON.stringify(DEFAULT_FORM_STATE)));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pagesRes, filesRes] = await Promise.all([
        apiService.getLandingPages(),
        apiService.getFiles()
      ]);
      setPages(pagesRes);
      setFiles(filesRes);
    } catch (error) {
      console.error(error);
      notifications.show({ title: 'Error', message: 'Failed to load data', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await apiService.updateLandingPage(editingId, formData);
        notifications.show({ title: 'Success', message: 'Landing banner updated successfully!', color: 'green' });
      } else {
        await apiService.createLandingPage(formData);
        notifications.show({ title: 'Success', message: 'Landing banner configured successfully!', color: 'green' });
      }
      setEditingId(null);
      setFormData(JSON.parse(JSON.stringify(DEFAULT_FORM_STATE)));
      fetchData();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Failed to save landing banner', color: 'red' });
    }
  };

  const handleEdit = (page) => {
    setEditingId(page.id);
    let ids = [];
    try {
      ids = typeof page.media_file_ids === 'string' ? JSON.parse(page.media_file_ids) : (page.media_file_ids || []);
    } catch (e) {}

    const formatForInput = (d) => {
      if (!d) return getDefaultDateTime();
      const date = new Date(d);
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    };

    let config = {};
    try {
      config = typeof page.content_config === 'string' ? JSON.parse(page.content_config) : (page.content_config || {});
    } catch (e) {}

    const cleanedSlidesMeta = {};
    const rawMeta = config.slidesMeta || {};
    Object.keys(rawMeta).forEach((k) => {
      const m = rawMeta[k] || {};
      cleanedSlidesMeta[k] = {
        title: m.title,
        targetUrl: m.targetUrl || '',
        buttonText: m.buttonText,
        lang: m.lang || null,
      };
    });

    setFormData({
      target_url: page.target_url || '',
      media_file_ids: ids,
      start_date: formatForInput(page.start_date),
      end_date: formatForInput(page.end_date),
      content_config: {
        slidesMeta: cleanedSlidesMeta
      }
    });
  };

  const handleDelete = (id) => {
    modals.openConfirmModal({
      title: 'Delete Landing Banner',
      centered: true,
      children: 'Are you sure you want to delete this landing banner?',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await apiService.deleteLandingPage(id);
          notifications.show({ title: 'Deleted', message: 'Landing banner deleted successfully', color: 'green' });
          if (editingId === id) {
            setEditingId(null);
            setFormData(JSON.parse(JSON.stringify(DEFAULT_FORM_STATE)));
          }
          fetchData();
        } catch (error) {
          notifications.show({ title: 'Error', message: 'Failed to delete landing banner', color: 'red' });
        }
      }
    });
  };

  const handeCancelEdit = () => {
    setEditingId(null);
    setFormData(JSON.parse(JSON.stringify(DEFAULT_FORM_STATE)));
  };

  const formatLocalDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  const setConfigVal = (key, val) => {
    setFormData(prev => ({
      ...prev,
      content_config: { ...prev.content_config, [key]: val }
    }));
  };

  const toggleMediaSelection = (f) => {
    setFormData(prev => {
      const isSelected = prev.media_file_ids.includes(f.id);
      const newIds = isSelected
        ? prev.media_file_ids.filter(id => id !== f.id)
        : [...prev.media_file_ids, f.id];

      const newSlidesMeta = { ...prev.content_config.slidesMeta };
      if (!isSelected && !newSlidesMeta[f.id]) {
        newSlidesMeta[f.id] = {
          title: { en: '', de: '' },
          targetUrl: '',
          buttonText: { en: '', de: '' },
          lang: null,
        };
      }
      return {
        ...prev,
        media_file_ids: newIds,
        content_config: { ...prev.content_config, slidesMeta: newSlidesMeta }
      };
    });
  };

  if (loading) return <div role="status" aria-live="polite">Loading…</div>;

  return (
    <div className="admin-landing-pages">
      <h2>Configure Landing / Event Banner</h2>

      <form onSubmit={handleSubmit} className="landing-page-form" noValidate>
        <div className="form-group">
          <label id="landing-media-label">Select Media (Image/Video uploaded in Media Center) *</label>
          <div className="media-selector" role="group" aria-labelledby="landing-media-label">
            {files.filter(f => f.mimetype && (f.mimetype.startsWith('image/') || f.mimetype.startsWith('video/'))).map(f => {
              const selectedIdx = formData.media_file_ids.indexOf(f.id);
              const isSelected = selectedIdx !== -1;
              return (
                <div
                  key={f.id}
                  className={`media-item ${isSelected ? 'selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-label={`${isSelected ? 'Unselect' : 'Select'} media ${f.filename} (${isSelected ? `slot ${selectedIdx + 1}` : 'unselected'})`}
                  onClick={() => toggleMediaSelection(f)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleMediaSelection(f);
                    }
                  }}
                >
                  {isSelected ? <div className="selection-badge" aria-hidden="true">{selectedIdx + 1}</div> : null}
                  {f.mimetype && f.mimetype.startsWith('video') ? (
                    <video src={`${API_BASE_URL}/files/${f.id}`} className="media-thumb" muted aria-hidden="true" />
                  ) : (
                    <img src={`${API_BASE_URL}/files/${f.id}`} alt="" className="media-thumb" />
                  )}
                  <span>{f.filename}</span>
                </div>
              );
            })}
          </div>
        </div>

        {formData.media_file_ids.length > 0 && (
          <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
            <h4 style={{ marginTop: 0 }}>Slide Configurations (Optional)</h4>
            <p style={{ marginTop: 0, fontSize: '0.85rem', color: '#666' }}>
              Title becomes the heading on the card. Target link becomes the button destination. Both are optional.
            </p>
            {formData.media_file_ids.map((fileId, index) => {
              const f = files.find(x => x.id === fileId);
              const rawMeta = formData.content_config.slidesMeta[fileId] || {};
              const safeField = (fieldVal) => {
                if (!fieldVal) return { en: "", de: "" };
                if (typeof fieldVal === "string") return { en: fieldVal, de: "" };
                return { en: fieldVal.en || "", de: fieldVal.de || "" };
              };
              const meta = {
                title: safeField(rawMeta.title),
                targetUrl: rawMeta.targetUrl || "",
                buttonText: safeField(rawMeta.buttonText),
                lang: rawMeta.lang || null,
              };
              const updateMeta = (patch) => {
                setConfigVal('slidesMeta', {
                  ...formData.content_config.slidesMeta,
                  [fileId]: { ...rawMeta, ...patch },
                });
              };
              return (
                <div key={fileId} style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #ddd' }}>
                  <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                    <span className="selection-badge" style={{ position: 'static', display: 'inline-block' }}>{index + 1}</span> {f?.filename}
                  </div>

                  <div className="form-row" style={{ alignItems: 'center', marginBottom: '10px', gap: '15px' }}>
                    <input type="text" placeholder="Title (EN) — optional" value={meta.title.en} style={{ flex: 1 }} onChange={e => updateMeta({ title: { ...meta.title, en: e.target.value } })} />
                    <input type="text" placeholder="Title (DE) — optional" value={meta.title.de} style={{ flex: 1 }} onChange={e => updateMeta({ title: { ...meta.title, de: e.target.value } })} />
                  </div>
                  <div className="form-row" style={{ alignItems: 'center', marginBottom: '10px', gap: '15px' }}>
                    <input type="text" placeholder="Target Link (e.g. /products/xyz) — optional" value={meta.targetUrl} style={{ flex: 1 }} onChange={e => updateMeta({ targetUrl: e.target.value })} />
                    <input type="text" placeholder='Button Text (EN) — optional, defaults to "Shop Now"' value={meta.buttonText.en} style={{ flex: 1 }} onChange={e => updateMeta({ buttonText: { ...meta.buttonText, en: e.target.value } })} />
                    <input type="text" placeholder='Button Text (DE) — optional, defaults to "Shop Now"' value={meta.buttonText.de} style={{ flex: 1 }} onChange={e => updateMeta({ buttonText: { ...meta.buttonText, de: e.target.value } })} />
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <Select
                      label="Language"
                      data={[
                        { value: '', label: 'All languages' },
                        { value: 'en', label: 'English (EN)' },
                        { value: 'de', label: 'Deutsch (DE)' },
                      ]}
                      value={meta.lang || ''}
                      onChange={(val) => updateMeta({ lang: val || null })}
                      description={'"All" shows the image regardless of the viewer\u2019s language.'}
                      allowDeselect={false}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <hr style={{ margin: '20px 0' }} />

        <div className="form-row">
          <div className="form-group half">
            <label htmlFor="landing-start-date">Banner Start Date &amp; Time *</label>
            <input
              id="landing-start-date"
              type="datetime-local"
              required
              value={formData.start_date}
              onChange={e => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>
          <div className="form-group half">
            <label htmlFor="landing-end-date">Banner End Date &amp; Time *</label>
            <input
              id="landing-end-date"
              type="datetime-local"
              required
              value={formData.end_date}
              onChange={e => setFormData({ ...formData, end_date: e.target.value })}
            />
          </div>
        </div>

        <Button type="submit" disabled={formData.media_file_ids.length === 0}>
          {editingId ? 'Update Landing Banner' : 'Save Landing Banner'}
        </Button>
        {editingId ? <Button variant="subtle" color="gray" onClick={handeCancelEdit} ml="sm">Cancel Edit</Button> : null}
      </form>

      <hr />

      <h3>Configured Landing Banners</h3>
      <table className="admin-table">
        <caption className="sr-only">Configured landing banners with media count, schedule, and actions</caption>
        <thead>
          <tr>
            <th scope="col">Media</th>
            <th scope="col">Starts</th>
            <th scope="col">Ends</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pages.map(p => {
            let mediaCount = 0;
            try {
              let ids = typeof p.media_file_ids === 'string' ? JSON.parse(p.media_file_ids) : p.media_file_ids;
              mediaCount = Array.isArray(ids) ? ids.length : 0;
            } catch (e) {}

            return (
              <tr key={p.id}>
                <th scope="row">{mediaCount} files</th>
                <td>{formatLocalDate(p.start_date)}</td>
                <td>{formatLocalDate(p.end_date)}</td>
                <td>
                  <Button variant="subtle" size="sm" leftSection={<IconEdit size={14} aria-hidden="true" />} aria-label={`Edit landing banner starting ${formatLocalDate(p.start_date)}`} onClick={() => handleEdit(p)}>Edit</Button>
                  <Button variant="light" color="red" size="sm" leftSection={<IconTrash size={14} aria-hidden="true" />} aria-label={`Delete landing banner starting ${formatLocalDate(p.start_date)}`} onClick={() => handleDelete(p.id)}>Delete</Button>
                </td>
              </tr>
            );
          })}
          {pages.length === 0 && (
            <tr><td colSpan="4">No landing banners configured.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}